import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../../components/Layout/Layout';
import {
  Card, Button, Badge, SearchInput, Spinner, EmptyState,
  Table, TableHead, TableBody, TableRow, TableCell, Modal, Tooltip
} from '../../components/UI';
import { productsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { HiOutlinePlus, HiOutlineCube, HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi';
import ProductModal from './ProductModal';

// Build compact pagination with ellipsis
const buildPageList = (current, total) => {
  const pages = [];
  const set = new Set([1, total, current, current - 1, current + 1]);
  for (const p of set) {
    if (p >= 1 && p <= total) pages.push(p);
  }
  pages.sort((a, b) => a - b);

  const result = [];
  for (let i = 0; i < pages.length; i++) {
    result.push(pages[i]);
    if (i < pages.length - 1 && pages[i + 1] - pages[i] > 1) {
      result.push('...');
    }
  }
  return result;
};

const ProductsList = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // Fetch products
  const { data: productsData, isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const response = await productsAPI.getAll();
      return response.data?.data || [];
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => productsAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['products']);
      queryClient.invalidateQueries(['scripts']);
      toast.success('Product deleted successfully');
      setShowDeleteModal(false);
      setProductToDelete(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete product');
    },
  });

  const products = productsData || [];

  const filteredProducts = search
    ? products.filter(p =>
        p.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.description?.toLowerCase().includes(search.toLowerCase())
      )
    : products;

  // Client-side pagination
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / pageSize));
  const safePage = Math.min(page, totalPages);
  if (safePage !== page) {
    setPage(safePage);
  }
  const paginatedProducts = filteredProducts.slice((safePage - 1) * pageSize, safePage * pageSize);

  const handleAdd = () => {
    setSelectedProduct(null);
    setShowModal(true);
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (productToDelete) {
      deleteMutation.mutate(productToDelete._id);
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">Products</h1>
            <p className="text-gray-500 text-xs sm:text-sm mt-0.5 sm:mt-1">
              Manage products/services that telecaller scripts are tied to
            </p>
          </div>
          <Button
            variant="primary"
            size="sm"
            icon={<HiOutlinePlus className="w-4 h-4" />}
            onClick={handleAdd}
          >
            <span className="hidden sm:inline">Add Product</span>
            <span className="sm:hidden">Add</span>
          </Button>
        </div>

        {/* Search */}
        <Card padding="p-3 sm:p-4">
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            placeholder="Search products..."
          />
        </Card>

        {/* Products List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : filteredProducts.length === 0 ? (
          <Card>
            <EmptyState
              icon={<HiOutlineCube className="w-8 h-8 text-gray-400" />}
              title="No products found"
              description="Add your first product to start creating scripts for it"
            />
          </Card>
        ) : (
          <Card padding="p-0">
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHead>
                  <TableRow hoverable={false}>
                    <TableCell header>Name</TableCell>
                    <TableCell header>Description</TableCell>
                    <TableCell header>Status</TableCell>
                    <TableCell header>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedProducts.map((product) => (
                    <TableRow key={product._id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                            <HiOutlineCube className="w-5 h-5 text-primary-600" />
                          </div>
                          <span className="font-medium text-gray-900">{product.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-gray-600 line-clamp-2 max-w-md">
                          {product.description || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={product.isActive !== false ? 'success' : 'danger'} size="sm">
                          {product.isActive !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Tooltip text="Edit Product">
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<HiOutlinePencil className="w-4 h-4" />}
                              onClick={(e) => { e.stopPropagation(); handleEdit(product); }}
                            />
                          </Tooltip>
                          <Tooltip text="Delete Product">
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                              onClick={(e) => { e.stopPropagation(); handleDeleteClick(product); }}
                            />
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {filteredProducts.map((product) => (
                <div key={product._id} className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                      <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                        <HiOutlineCube className="w-5 h-5 text-primary-600" />
                      </div>
                      <p className="font-medium text-gray-900 truncate">{product.name}</p>
                    </div>
                    <Badge variant={product.isActive !== false ? 'success' : 'danger'} size="sm">
                      {product.isActive !== false ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  {product.description && (
                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">{product.description}</p>
                  )}
                  <div className="flex items-center justify-end gap-1 pt-2 border-t border-gray-100">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2"
                      icon={<HiOutlinePencil className="w-4 h-4 text-gray-500" />}
                      onClick={() => handleEdit(product)}
                      title="Edit"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-2"
                      icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                      onClick={() => handleDeleteClick(product)}
                      title="Delete"
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
<div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 sm:p-4 border-t border-gray-100">
  <div className="flex flex-wrap sm:flex-nowrap items-center gap-3 text-xs sm:text-sm text-gray-500">
    <span>
      Showing {(safePage - 1) * pageSize + 1}–
      {Math.min(safePage * pageSize, filteredProducts.length)} of{" "}
      {filteredProducts.length}
    </span>

    <div className="inline-flex items-center gap-2 whitespace-nowrap shrink-0">
      <label
        htmlFor="script-rows-per-page"
        className="text-xs sm:text-sm text-gray-500 whitespace-nowrap"
      >
        Rows per page:
      </label>

      <select
        id="prod-rows-per-page"
        value={pageSize}
        onChange={(e) => {
          setPageSize(Number(e.target.value));
          setPage(1);
        }}
        className="w-20 px-2 py-1 border border-gray-200 rounded-md text-sm bg-white shrink-0"
      >
        <option value={10}>10</option>
        <option value={25}>25</option>
        <option value={50}>50</option>
        <option value={100}>100</option>
      </select>
    </div>
  </div>

  {totalPages > 1 && (
    <div className="flex items-center gap-1.5 sm:gap-2">
      <Button
        variant="outline"
        size="sm"
        disabled={safePage === 1}
        onClick={() => setPage((p) => p - 1)}
      >
        <span className="hidden sm:inline">Previous</span>
        <span className="sm:hidden">Prev</span>
      </Button>

      {buildPageList(safePage, totalPages).map((item, idx) =>
        item === "..." ? (
          <span
            key={`ellipsis-${idx}`}
            className="px-2 text-sm text-gray-400 select-none"
          >
            …
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => setPage(item)}
            className={
              safePage === item
                ? "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-semibold bg-primary-600 text-white transition-colors"
                : "min-w-[34px] px-2.5 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            }
          >
            {item}
          </button>
        )
      )}

      <Button
        variant="outline"
        size="sm"
        disabled={safePage === totalPages}
        onClick={() => setPage((p) => p + 1)}
      >
        Next
      </Button>
    </div>
  )}
</div>
          </Card>
        )}

        {/* Product Modal */}
        <ProductModal
          isOpen={showModal}
          onClose={() => { setShowModal(false); setSelectedProduct(null); }}
          product={selectedProduct}
          onSuccess={() => queryClient.invalidateQueries(['products'])}
        />

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete Product"
          size="sm"
        >
          <div className="space-y-3 sm:space-y-4">
            <p className="text-gray-600 text-sm">
              Are you sure you want to delete{' '}
              <span className="font-semibold">{productToDelete?.name}</span>?
              This will also remove it from any scripts that reference it.
            </p>
            <div className="flex gap-2 sm:gap-3 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={handleConfirmDelete}
                loading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

export default ProductsList;